import { api } from "@repo/convex/api";
import type { Id } from "@repo/convex/dataModel";
import { formatUserIdentity, getInitials, UNRESOLVED_USER_LABEL } from "@repo/shared";
import { useQuery } from "convex/react";
import { useMemo } from "react";
import { useOrganization } from "@/contexts/organization-context";

// ============================================================================
// Organization User Map Hook
// ============================================================================
// Provides an efficient lookup map from user IDs to display info.
// Powered by a single Convex subscription that React deduplicates across
// all components using this hook — no matter how many call it, only one
// WebSocket subscription to listByOrganization exists.

export interface UserDisplayInfo {
  fullName: string;
  initials: string;
  email: string;
  systemKey?: string;
  profilePictureUrl?: string;
  membershipStatus?: "active" | "inactive" | "pending";
}

/**
 * Build a Map<userId, UserDisplayInfo> for the current organization.
 *
 * Returns `null` while loading or if no organization is selected.
 * Once loaded, lookups are O(1) by user ID string.
 *
 * @example
 * const userMap = useOrgUserMap();
 * const { fullName, initials } = resolveUser(userMap, entity.createdBy);
 */
export function useOrgUserMap(): Map<string, UserDisplayInfo> | null {
  const { organizationId } = useOrganization();
  const users = useQuery(
    api.users.listByOrganization,
    organizationId ? { orgId: organizationId } : "skip"
  );

  return useMemo(() => {
    if (!users) return null;
    const map = new Map<string, UserDisplayInfo>();
    for (const user of users) {
      const { displayName, initials } = formatUserIdentity(user);
      map.set(user._id, {
        fullName: displayName,
        initials,
        email: user.email,
        systemKey: user.systemKey ?? undefined,
        profilePictureUrl: user.profilePictureUrl ?? undefined,
        membershipStatus: user.membershipStatus,
      });
    }
    return map;
  }, [users]);
}

/** An id no member row answers to — not the same thing as a member with no name. */
const UNRESOLVED_USER: UserDisplayInfo = {
  fullName: UNRESOLVED_USER_LABEL,
  initials: getInitials(null),
  email: "",
};

/**
 * Resolve a user ID to display name and initials.
 *
 * An id this map cannot place — still loading, or a person outside the organization — is
 * unresolved rather than nameless; a member the map *does* hold always has a name, because
 * the shared rule falls back to their email.
 */
export function resolveUser(
  userMap: Map<string, UserDisplayInfo> | null,
  userId: Id<"users"> | string | undefined | null
): UserDisplayInfo {
  if (!userId || !userMap) return UNRESOLVED_USER;
  return userMap.get(userId) ?? UNRESOLVED_USER;
}
